import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { heroImages, modelInfo } from '../data/mock';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    setTimeout(() => setIsTransitioning(false), 700);
  }, [isTransitioning]);

  const prevSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
    setTimeout(() => setIsTransitioning(false), 700);
  }, [isTransitioning]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  return (
    <section id="home" className="relative h-screen w-full overflow-hidden bg-black">
      {/* Slides */}
      {heroImages.map((image, index) => (
        <div
          key={image.id}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          <img
            src={image.url}
            alt={image.alt}
            loading={index === 0 ? "eager" : "lazy"}
            className={`w-full h-full object-contain md:object-contain ${index === 1 ? 'scale-130' : ''}`}
            style={index === 1 ? { transform: 'scale(1.5)' } : {}}
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/20" />
        </div>
      ))}

      {/* Content */}
      <div className="absolute inset-0 z-20 flex items-end justify-center pb-32 md:pb-40">
        <div className="text-center text-white px-6">
          <h1 className="font-bebas text-5xl md:text-7xl lg:text-8xl tracking-[0.2em] mb-4 animate-fade-in drop-shadow-lg">
            {modelInfo.name.toUpperCase()}
          </h1>
          <p className="font-poppins text-xs md:text-sm tracking-[0.4em] opacity-90 drop-shadow-md">
            {modelInfo.tagline.toUpperCase()}
          </p>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-6 top-1/2 -translate-y-1/2 z-30 p-3 text-white/70 hover:text-white transition-all duration-300 hover:scale-110"
        aria-label="Previous slide"
      >
        <ChevronLeft size={40} strokeWidth={1} />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-6 top-1/2 -translate-y-1/2 z-30 p-3 text-white/70 hover:text-white transition-all duration-300 hover:scale-110"
        aria-label="Next slide"
      >
        <ChevronRight size={40} strokeWidth={1} />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex space-x-3">
        {heroImages.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              if (!isTransitioning) {
                setIsTransitioning(true);
                setCurrentSlide(index);
                setTimeout(() => setIsTransitioning(false), 700);
              }
            }}
            className={`w-12 h-[2px] transition-all duration-300 ${
              index === currentSlide ? 'bg-white' : 'bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 right-8 z-30 hidden md:block">
        <div className="flex flex-col items-center text-white/60">
          <span className="font-poppins text-xs tracking-widest rotate-90 origin-center mb-12">SCROLL</span>
          <div className="w-[1px] h-16 bg-white/40 animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
